import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { removeOrderCheckoutLocalData } from './site';
import { LAST_USER_ACTIVITY_KEY } from '../constants/localStorageKeys';
import { isColorSchemeDetectionSupported } from './helpers';
import { RequestOptions } from '../types/httpRequests';
import { NavigateFunction } from 'react-router-dom';
import { AccountType, EnrichedAccount } from '../types/account';
import { OrganizationType } from '../types/organization';

const backendURL = process.env.REACT_APP_BACKEND_URL;

export async function fetchCognitoUserToken(): Promise<string> {
  try {
    // if the session has already expired, Cognito will get a new one
    // and this will work as far as the refresh token is valid(30 days)
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() || '';
  } catch (error) {
    console.error('Error fetching accessToken:', error);
    return '';
  }
}

export async function fetchCognitoUserIDToken() {
  try {
    // if the session has already expired, Cognito will get a new one
    // and this will work as far as the refresh token is valid(30 days)
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Error fetching idToken:', error);
    return '';
  }
}

export const addIntCodeToPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return '';

  if (phoneNumber.startsWith('+')) {
    return phoneNumber;
  }
  return `+1${phoneNumber}`;
};

export async function handleUserSignOut() {
  try {
    await signOut();
    removeOrderCheckoutLocalData();
    localStorage.removeItem(LAST_USER_ACTIVITY_KEY);
  } catch (error) {
    console.log('error signing out: ', error);
  }
}

export const getInitialUserThemeValue = () => {
  if (isColorSchemeDetectionSupported()) {
    // System theme detection is supported, use that
    return 'system';
  } else {
    // System theme detection not supported, default to light
    return 'light';
  }
};

export const sendConfirmationCode = async (sendConfirmationPayload: any, apiFetchRequest: any) => {
  const endpointUrl: string = `${backendURL}/users/send-confirmation-code`;
  const apiRequestOptions: RequestOptions = {
    body: sendConfirmationPayload,
    method: 'POST',
    endpoint: endpointUrl,
  };

  return await apiFetchRequest(apiRequestOptions);
};

export const sendOtp = async (sendOtpPayload: any, apiFetchRequest: any) => {
  const endpointUrl: string = `${backendURL}/users/send-otp`;
  const apiRequestOptions: RequestOptions = {
    body: sendOtpPayload,
    method: 'POST',
    endpoint: endpointUrl,
  };

  return await apiFetchRequest(apiRequestOptions);
};

export const validateOtp = async (sendOtpPayload: any, apiFetchRequest: any) => {
  const endpointUrl: string = `${backendURL}/users/validate-otp`;
  const apiRequestOptions: RequestOptions = {
    body: sendOtpPayload,
    method: 'POST',
    endpoint: endpointUrl,
  };

  return await apiFetchRequest(apiRequestOptions);
};

export const fetchOrgsAndAccounts = async (
  setErrorText: (text: string | null) => void,
  setLoadingLocal: (loading: boolean) => void,
  apiFetchRequest: (req: RequestOptions) => Promise<any>,
  setOrgs: (orgs: OrganizationType[]) => void,
  setAccounts: (accounts: EnrichedAccount[]) => void,
  navigate: NavigateFunction,
): Promise<void> => {
  setErrorText(null);
  setLoadingLocal(true);
  const req: RequestOptions = {
    endpoint: `${backendURL}/users/orgs-accounts`,
    method: 'GET',
  };

  try {
    const data = await apiFetchRequest(req);
    if (!data) {
      setErrorText('Something went wrong. Please try again.');
      setOrgs([]);
      setAccounts([]);
      return;
    }

    const { status, body: responseBody, isError } = data as { status: number; body: any; isError?: boolean };
    const statusCode = responseBody?.status_code ?? status;

    if (statusCode === 401) {
      navigate('/401.html');
      return;
    }

    if (isError || statusCode >= 500) {
      setErrorText('Something went wrong. Please try again.');
      setOrgs([]);
      setAccounts([]);
      return;
    }

    const body = responseBody ?? {};
    const orgMessage: string | undefined = body.org_message;
    const accountMessage: string | undefined = body.account_message;

    if (orgMessage === 'UNAUTHENTICATED' || accountMessage === 'UNAUTHENTICATED') {
      navigate('/401.html');
      return;
    }

    const organizationsPayload = Array.isArray(body.organizations) ? (body.organizations as OrganizationType[]) : [];
    const accountsPayload = Array.isArray(body.accounts) ? (body.accounts as AccountType[]) : [];

    let nextOrgs: OrganizationType[] = [];
    let nextAccounts: EnrichedAccount[] = [];
    let nextError: string | null = null;

    switch (orgMessage) {
      case 'SUCCESS':
        if (Array.isArray(body.organizations)) {
          nextOrgs = organizationsPayload;
        } else {
          nextError = 'Something went wrong. Please try again.';
        }
        break;
      case 'NO_ORGS':
      case 'NO_ORGS_AUTHORIZED':
        nextOrgs = [];
        break;
      case undefined:
        nextError = 'Something went wrong. Please try again.';
        nextOrgs = [];
        break;
      default:
        nextError = 'Something went wrong. Please try again.';
        nextOrgs = [];
        break;
    }

    const orgPathById = new Map<number, string>(organizationsPayload.map((org) => [org.org_id, org.path_name ?? '']));

    switch (accountMessage) {
      case 'SUCCESS':
        if (Array.isArray(body.accounts)) {
          nextAccounts = accountsPayload.map((account) => {
            const resolvedOrgPath = account.org_path_name ?? orgPathById.get(account.org_id) ?? '';
            return { ...account, __orgPath: resolvedOrgPath };
          });
        } else {
          nextError = nextError ?? 'Something went wrong. Please try again.';
          nextAccounts = [];
        }
        break;
      case 'NO_ACCOUNTS':
      case 'NO_ACCOUNTS_AUTHORIZED':
        nextAccounts = [];
        break;
      case undefined:
        nextError = nextError ?? 'Something went wrong. Please try again.';
        nextAccounts = [];
        break;
      default:
        nextError = nextError ?? 'Something went wrong. Please try again.';
        nextAccounts = [];
        break;
    }

    setOrgs(nextOrgs);
    setAccounts(nextAccounts);
    setErrorText(nextError);
  } catch {
    setErrorText('Something went wrong. Please try again.');
    setOrgs([]);
    setAccounts([]);
  } finally {
    setLoadingLocal(false);
  }
};
